import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from "react-router-dom";
import { Heading, Text, Input, Button, GridRow, GridColumn, GridWrapper, ArrowLink } from 'react-onei';

import Captcha from '../../Captcha/Captcha';
import TagService from '../../../services/TagService';

import ApiService from '../../../services/ApiService';

import UserContext from '../../../context/UserContext';
import { events } from '../../../conf/tags_ga.conf';
import { addQueryStringParams } from "../../../utils";
/**
 * Create a string of selected captcha images values
 * @param selectedImages
 * @return {string}
 */
const createQueryCaptcha = (selectedImages) => {
  if (selectedImages && selectedImages.length) {
    const values = selectedImages.map(image => image.value);
    return `${values.join('-')}-`;
  }
  return '-';
};

export class LoginView extends Component {
  constructor(props, context) {
    super(props, context);
    const { identities } = context;

    this.state = {
      login: identities.login,
      indications: [],
      images: [],
      selectedImages: [],
      isFetching: false,
      loginError: undefined,
      captchaError: null,
      isSubmitted: false,
    };
    this.loginInputRef = React.createRef();
  }

  /**
   * Set selected captcha image
   */
  setSelectedImages = (image) => {
    const { isSubmitted, selectedImages, captchaError } = this.state;

    if (isSubmitted) {
      this.setState({ isSubmitted: false });
    };
    if (captchaError) {
      this.setState({ captchaError: null });
    }
    if (selectedImages.length < 6) {
      this.setState(state => ({
        selectedImages: [...state.selectedImages, image],
      }));
    }
  }

  /**
   * Remove last selected captcha image
   */
  removeLastImage = () => {
    this.setState(prevState => {
      const images = prevState.selectedImages.slice();
      images.pop();
      return { selectedImages: images }
    });
  }

  async componentDidMount() {
    // Focus login input on mount
    this.loginInputRef.current.focus();

    // Generate captcha
    await this.generateCaptcha();
  }

  componentDidUpdate(prevProps, prevState) {
    const { captchaError } = this.state;
    const { wording } = this.context;
    const { WLOST_112 } = wording;

    if (prevState.captchaError !== captchaError) {
      if (captchaError === WLOST_112) {
        this.generateCaptcha();
      }
    }
  }

  /**
   * Generate new captcha
   */
  generateCaptcha = async () => {
    this.setState({ isFetching: true });
    try {
      const res = await ApiService.createCaptcha();
      if (res.status === 201) {
        const { data } = res.data;
        this.setState({
          images: data.rows,
          indications: data.indications
        });
      }
    } catch (err) {
      // No error when captcha handle error
    }
    this.setState({ isFetching: false });
  }

  /**
   * Handle error when create identities
   * @param {*} err 
   */
  handleError = (err) => {
    const field = err.response?.data?.field;
    const code = err.response?.data?.code;
    const { history } = this.props;
    const { wording, webViewMode } = this.context;
    const { WLOST_107, WLOST_111, WLOST_104, WLOST_112, WLOST_105 } = wording;
    switch (code) {
      case 403006:
        // Detect compromised accounts, redirect to recovery
        let redirectUrl = err.response?.data?.data?.redirectUrl;
        if (webViewMode) {
          redirectUrl = addQueryStringParams(redirectUrl, { webViewMode: '1.1' });
        }
        window.location.href = redirectUrl;
        break;
      case 412002:
        this.setState({ loginError: WLOST_107 }) // Empty login
        break;
      case 412004:
        if (field === 'captcha') {
          TagService.sendEvent(events.captchaUnfinished);
          this.setState({ captchaError: WLOST_111 })
        } else {
          TagService.sendEvent(events.loginInvalid);
          this.setState({ loginError: WLOST_104 }) // Invalid syntax login
        }
        break;
      case 412005:
        if (field === 'captcha') {
          TagService.sendEvent(events.captchaInvalid);
          this.setState({
            captchaError: WLOST_112,
            selectedImages: [],
            indications: [],
            images: [],
          });
        } else {
          TagService.sendEvent(events.loginUnknown);
          this.setState({ loginError: WLOST_105 })
        }
        break;
      default:
        history.push({
          pathname: '/error',
          state: {
            code,
          }
        });
    }
  }

  /**
   * Check Orange login availability
   */
  checkCredentials = async () => {
    const { login } = this.state;
    const { wording } = this.context;
    const { WLOST_104 } = wording;
    const { history } = this.props;

    this.setState({ isFetching: true });
    try {
      await ApiService.checkCredentials({ login });
    } catch (err) {
      if (err.response.status === 412) {
        this.setState({ loginError: WLOST_104 }) // Invalid syntax login
      } else {
        history.push({
          pathname: '/error',
          state: {
            code: err.response?.data?.code,
          }
        });
      }

    }
    this.setState({ isFetching: false });
  }

  /**
   * Set login state and clear error when exist on input change
   * @param login
   */
  handleChange = (e) => {
    const { value } = e.target;
    const { loginError } = this.state;

    this.setState({
      login: value.trim(),
      isSubmitted: false,
    });
    if (value && loginError) {
      this.setState({ loginError: undefined });
    }
  };

  /**
   * Check login credentials on input blur, set empty error when no login is filled
   */
  handleBlur = (e) => {
    const { login } = this.state;
    const { wording } = this.context;
    const { WLOST_107 } = wording;

    if (e.relatedTarget && e.relatedTarget.type === "submit") {
      return
    } else {
      if (!login) {
        this.setState({ loginError: WLOST_107 }) // Empty login
      } else {
        this.checkCredentials();
      }
    }
  };

  handleClick = () => {
    TagService.sendEvent(events.findLogin);
  }

  /**
   * Handle submit
   */
  handleSubmit = async (e) => {
    e.preventDefault();
    const { login, selectedImages } = this.state;
    const { history } = this.props;
    const { wording, processIdentities, getStepChoice, setStep } = this.context;
    const { WLOST_107 } = wording;
    const captcha = createQueryCaptcha(selectedImages);

    TagService.sendEvent(events.login);
    this.setState({ isFetching: true });

    if (!login) {
      this.setState({ loginError: WLOST_107 });
      this.loginInputRef.current.blur();
    } else {
      try {
        const { identities, serviceConf, contacts } = await processIdentities(login, captcha);
        const step = await getStepChoice(identities, serviceConf, contacts);
        setStep(step);
        history.push(step);
      } catch (err) {
        this.handleError(err);
      }
    }
    this.setState({ isFetching: false });
  };

  render() {
    const { login, indications, images, selectedImages, loginError, captchaError, isFetching } = this.state;
    const { webViewMode, wording } = this.context;
    const { WLOST_101, WLOST_102, WLOST_103, WLOST_110, WBUTTON_001, WLOST_106, WLOST_114 } = wording;

    return (
      <GridWrapper>
        <form id="ident-form" onSubmit={this.handleSubmit} method="post">
          <GridRow>
            <GridColumn size={{ xs: 1, m: 8 / 12 }}>
              <Heading as="h3" id="login-subtitle" className="spacing-m">{WLOST_101}</Heading>
              <Text id="login-helper">
                {WLOST_102}
              </Text>
            </GridColumn>
          </GridRow>

          <GridRow>
            <GridColumn size={{ xs: 1, m: 6 / 12 }}>
              <Input
                autoFocus
                ref={this.loginInputRef}
                error={loginError}
                onBlur={this.handleBlur}
                onChange={this.handleChange}
                id="login-input"
                defaultValue={login}
                label={WLOST_103}
                displayOptionalLabel={false}
                maxLength={254}
              />
            </GridColumn>
          </GridRow>

          <GridRow>
            <GridColumn size={{ xs: 1, m: 8 / 12 }}>
              {!webViewMode && (
                <ArrowLink
                  href={window.reactAppEnv.href.findLogin}
                  id="find-login-link"
                  onClick={this.handleClick}
                  aria-label={WLOST_106}
                  title={WLOST_106}
                  fullWidth
                  className="spacing-xs"
                >
                  {WLOST_106}
                </ArrowLink>
              )}
            </GridColumn>
          </GridRow>
          {
            (indications.length > 0) && (
              <GridRow>
                <GridColumn size={{ xs: 1, m: 8 / 12 }}>
                  <Heading as="h3" id="captcha-subtitle" className="spacing-m">{WLOST_114}</Heading>
                  <Text id="captcha-helper">
                    {WLOST_110}
                  </Text>
                  <Captcha
                    errorMessage={captchaError}
                    indications={indications}
                    images={images}
                    selectedImages={selectedImages}
                    onSelectImage={this.setSelectedImages}
                    onRemoveImage={this.removeLastImage}
                  />
                </GridColumn>
              </GridRow>
            )
          }
          <GridRow>
            <GridColumn>
              <Button
                id="login-submit-button"
                disabled={isFetching}
                type="submit"
                className="spacing-s"
              >
                {WBUTTON_001}
              </Button>
            </GridColumn>
          </GridRow>
        </form >
      </GridWrapper >
    );
  }
}

LoginView.defaultProps = {
};

LoginView.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

LoginView.contextType = UserContext;

export default withRouter(LoginView);